// Reporte de Retenciones por Período. Financiero → Reportes.
// Cubre lo que al formulario 103 le falta: detalle línea a línea, las DOS
// direcciones (lo que retuve y lo que me retuvieron) y los dos impuestos.
// El resumen de EMITIDAS + RENTA de un mes calendario cuadra exactamente con
// contabilidad.trib_form_103 de ese mes.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertTriangle, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { ExportarBotones, useMarcaEmpresa } from '@/lib/utils/exportarTabla';
import { SortableTh, useSort } from './tablaSort';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const num = (v: number) => (Number(v) || 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Sentido = 'emitidas' | 'recibidas';

interface ResumenFila {
  impuesto: string; codigo: string; descripcion: string;
  lineas: number; comprobantes: number; base: number; retenido: number;
}
interface DetalleFila {
  fecha: string; comprobante: string; contraparte: string; identificacion: string;
  impuesto: string; codigo: string; descripcion: string;
  base: number; porcentaje: number; retenido: number;
  doc_sustento: string | null; doc_sustento_fecha: string | null;
}
interface Reporte {
  sentido: Sentido; desde: string; hasta: string;
  resumen: ResumenFila[]; detalle: DetalleFila[];
  total_base: number; total_retenido: number; lineas: number;
  truncado: boolean; pendientes: number; aviso_pendientes: string | null;
}

// Períodos de uso frecuente: la declaración es mensual, así que el mes cerrado
// anterior es lo que más se consulta.
function rangos() {
  const h = new Date();
  const f = (d: Date) => d.toISOString().slice(0, 10);
  const ini = (y: number, m: number) => f(new Date(Date.UTC(y, m, 1)));
  const fin = (y: number, m: number) => f(new Date(Date.UTC(y, m + 1, 0)));
  const y = h.getFullYear(), m = h.getMonth();
  return [
    { t: 'Mes actual', d: ini(y, m), h: fin(y, m) },
    { t: 'Mes anterior', d: ini(y, m - 1), h: fin(y, m - 1) },
    { t: 'Año actual', d: ini(y, 0), h: fin(y, 11) },
  ];
}

export function ReporteRetenciones() {
  const R = useMemo(rangos, []);
  const [desde, setDesde] = useState(R[1].d);
  const [hasta, setHasta] = useState(R[1].h);
  const [sentido, setSentido] = useState<Sentido>('emitidas');
  const [impuesto, setImpuesto] = useState('');
  const marca = useMarcaEmpresa();

  const { data, isFetching, error } = useQuery<Reporte>({
    queryKey: ['ret-reporte', desde, hasta, sentido, impuesto],
    enabled: !!desde && !!hasta && hasta >= desde,
    queryFn: async () => (await apiClient.post('/rpc/fin_retenciones_reporte', {
      _desde: desde, _hasta: hasta, _sentido: sentido, _impuesto: impuesto || null,
    }, FIN)).data,
  });

  const resumen = data?.resumen ?? [];
  const detalle = data?.detalle ?? [];
  const { sorted: resOrd, sort: sortRes, toggle: togRes } = useSort(resumen);
  const { sorted: detOrd, sort: sortDet, toggle: togDet } = useSort(detalle);

  const esEmitida = sentido === 'emitidas';
  const etiquetaContraparte = esEmitida ? 'Proveedor (a quien retuve)' : 'Cliente (quien me retuvo)';

  const headersDet = ['Fecha', 'Comprobante', etiquetaContraparte, 'Identificación', 'Impuesto',
    'Código', 'Descripción', 'Base imponible', '%', 'Retenido', 'Documento sustentado'];
  const filasDet = detOrd.map((d) => [
    String(d.fecha).slice(0, 10), d.comprobante, d.contraparte, d.identificacion, d.impuesto,
    d.codigo, d.descripcion, Number(d.base), Number(d.porcentaje), Number(d.retenido), d.doc_sustento ?? '',
  ]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Retenciones por Período</h1>
          <p className="text-sm text-muted-foreground">
            {esEmitida
              ? 'Lo que retuviste a tus proveedores: es una obligación por pagar al fisco y sustenta el formulario 103.'
              : 'Lo que te retuvieron sobre tus ventas: es crédito tributario a tu favor.'}
          </p>
        </div>
        <ExportarBotones
          nombre={`retenciones-${sentido}-${desde}_${hasta}`}
          titulo={`Retenciones ${sentido} · ${desde} a ${hasta}`}
          subtitulo={`Base ${usd(Number(data?.total_base ?? 0))} · Retenido ${usd(Number(data?.total_retenido ?? 0))}`}
          headers={headersDet} filas={filasDet} colsDerecha={[7, 8, 9]}
          disabled={filasDet.length === 0} marca={marca}
        />
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex gap-1 rounded-lg border p-1">
            {([['emitidas', 'Emitidas (yo retuve)'], ['recibidas', 'Recibidas (me retuvieron)']] as const).map(([k, t]) => (
              <button key={k} onClick={() => setSentido(k)}
                className={`rounded-md px-3 py-1 text-sm ${sentido === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
            ))}
          </div>
          <label className="flex flex-col text-xs text-muted-foreground">Desde
            <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Hasta
            <input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Impuesto
            <select className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
              value={impuesto} onChange={(e) => setImpuesto(e.target.value)}>
              <option value="">Todos</option>
              <option value="RENTA">Renta</option>
              <option value="IVA">IVA</option>
              <option value="ISD">ISD</option>
            </select></label>
          <div className="flex gap-1">
            {R.map((r) => (
              <Button key={r.t} size="sm" variant="outline" onClick={() => { setDesde(r.d); setHasta(r.h); }}>{r.t}</Button>
            ))}
          </div>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        </CardContent>
      </Card>

      {hasta < desde && (
        <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          La fecha final no puede ser anterior a la inicial.
        </p>
      )}
      {error && (
        <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
          No se pudo generar el reporte.
        </p>
      )}

      {/* El aviso que evita declarar de menos: lo que quedó fuera y por qué */}
      {data?.aviso_pendientes && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {data.aviso_pendientes}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {([
          ['Base imponible', usd(Number(data?.total_base ?? 0)), 'sobre la que se retuvo'],
          [esEmitida ? 'Retenido (por pagar al SRI)' : 'Retenido (crédito a favor)', usd(Number(data?.total_retenido ?? 0)), ''],
          ['Líneas', String(data?.lineas ?? 0), `${resumen.length} código${resumen.length === 1 ? '' : 's'}`],
          ['Comprobantes', String(resumen.reduce((s, r) => s + Number(r.comprobantes), 0)), 'en el período'],
        ] as const).map(([t, v, sub]) => (
          <Card key={t}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t}</p>
            <p className="text-2xl font-semibold tabular-nums">{v}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Percent className="w-4 h-4" /> Resumen por código
          </CardTitle>
          {esEmitida && (
            <p className="text-xs text-muted-foreground">
              Con impuesto «Renta» y un mes calendario, estos totales son exactamente los del formulario 103.
            </p>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {resumen.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin retenciones en este período.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3 text-left w-10">N°</th>
                  <SortableTh col="impuesto" label="Impuesto" sort={sortRes} onSort={togRes} />
                  <SortableTh col="codigo" label="Código" sort={sortRes} onSort={togRes} />
                  <SortableTh col="descripcion" label="Concepto" sort={sortRes} onSort={togRes} />
                  <SortableTh col="comprobantes" label="Comprobantes" align="right" sort={sortRes} onSort={togRes} />
                  <SortableTh col="lineas" label="Líneas" align="right" sort={sortRes} onSort={togRes} />
                  <SortableTh col="base" label="Base imponible" align="right" sort={sortRes} onSort={togRes} />
                  <SortableTh col="retenido" label="Retenido" align="right" sort={sortRes} onSort={togRes} />
                </tr>
              </thead>
              <tbody>
                {resOrd.map((r, i) => (
                  <tr key={`${r.impuesto}-${r.codigo}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{r.impuesto}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.codigo}</td>
                    <td className="py-2 pr-3">{r.descripcion || <span className="text-muted-foreground">— sin descripción en el catálogo —</span>}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.comprobantes}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.lineas}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{num(Number(r.base))}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{num(Number(r.retenido))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={6} className="py-2 pr-3 text-right text-muted-foreground">Totales</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(Number(data?.total_base ?? 0))}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(Number(data?.total_retenido ?? 0))}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Detalle ({detalle.length})</CardTitle>
          {data?.truncado && (
            <p className="text-xs text-amber-600">
              Se muestran las primeras {detalle.length} de {data.lineas} líneas. Los totales y el resumen
              sí cubren el período completo; acota el rango de fechas para ver el resto del detalle.
            </p>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {detalle.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin líneas que mostrar.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3 text-left w-10">N°</th>
                  <SortableTh col="fecha" label="Fecha" sort={sortDet} onSort={togDet} />
                  <SortableTh col="comprobante" label="Comprobante" sort={sortDet} onSort={togDet} />
                  <SortableTh col="contraparte" label={etiquetaContraparte} sort={sortDet} onSort={togDet} />
                  <SortableTh col="impuesto" label="Impuesto" sort={sortDet} onSort={togDet} />
                  <SortableTh col="codigo" label="Código" sort={sortDet} onSort={togDet} />
                  <SortableTh col="base" label="Base" align="right" sort={sortDet} onSort={togDet} />
                  <SortableTh col="porcentaje" label="%" align="right" sort={sortDet} onSort={togDet} />
                  <SortableTh col="retenido" label="Retenido" align="right" sort={sortDet} onSort={togDet} />
                  <SortableTh col="doc_sustento" label="Documento sustentado" sort={sortDet} onSort={togDet} />
                </tr>
              </thead>
              <tbody>
                {detOrd.map((d, i) => (
                  <tr key={`${d.comprobante}-${d.codigo}-${i}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{String(d.fecha).slice(0, 10)}</td>
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.comprobante}</td>
                    <td className="py-2 pr-3">
                      {d.contraparte}
                      <span className="block text-xs text-muted-foreground">{d.identificacion}</span>
                    </td>
                    <td className="py-2 pr-3">{d.impuesto}</td>
                    <td className="py-2 pr-3 font-mono text-xs" title={d.descripcion}>{d.codigo}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{num(Number(d.base))}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{Number(d.porcentaje)}%</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{num(Number(d.retenido))}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{d.doc_sustento || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t font-medium">
                <tr>
                  <td colSpan={6} className="py-2 pr-3 text-right text-muted-foreground">
                    {data?.truncado ? 'Totales de lo mostrado' : 'Totales'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(detalle.reduce((s, d) => s + Number(d.base), 0))}</td>
                  <td />
                  <td className="py-2 pr-3 text-right tabular-nums">{num(detalle.reduce((s, d) => s + Number(d.retenido), 0))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReporteRetenciones;
